import { z } from 'zod';
import { MenuKey } from '../enums';
import { patchSchemaOf, uuidSchema } from './common';
/**
 * A menu item points at exactly one destination: an internal page (by id) or an
 * external URL. Encoding that as "exactly one of two nullable columns" is what
 * keeps the admin UI honest — you cannot save an item that links nowhere, nor
 * one that links to two places.
 */
const menuItemBaseSchema = z.object({
    label: z.string().trim().min(1).max(60),
    pageId: uuidSchema.nullish(),
    externalUrl: z.url().max(500).nullish(),
    /** Nesting is supported one level deep, which is all the design calls for. */
    parentId: uuidSchema.nullish(),
    sortOrder: z.number().int().min(0).default(0),
    isVisible: z.boolean().default(true),
    openInNewTab: z.boolean().default(false),
    /** Renders the item as a filled CTA button rather than a plain link. */
    isCta: z.boolean().default(false),
});
const exactlyOneDestination = (data) => {
    const hasPage = typeof data.pageId === 'string' && data.pageId.length > 0;
    const hasUrl = typeof data.externalUrl === 'string' && data.externalUrl.length > 0;
    return hasPage !== hasUrl;
};
export const createMenuItemSchema = menuItemBaseSchema
    .extend({ menuKey: z.enum(MenuKey) })
    .refine(exactlyOneDestination, {
    path: ['pageId'],
    error: 'Link this item to either a page or an external URL — exactly one.',
});
/**
 * Every field optional — and genuinely optional.
 *
 * `patchSchemaOf` rather than `.partial()`: four of these fields carry a `.default()`, and
 * `.partial()` keeps the default firing when the key is absent. That is how a request
 * sending only `{ isVisible }` used to reset the entry's `sortOrder` to 0 and turn a CTA
 * entry into a plain link. See the note on `patchSchemaOf`.
 */
export const updateMenuItemSchema = patchSchemaOf(menuItemBaseSchema.shape);
/** Drag-and-drop result for one menu: the full ordered tree in one request. */
export const reorderMenuSchema = z.object({
    menuKey: z.enum(MenuKey),
    items: z
        .array(z.object({
        id: uuidSchema,
        sortOrder: z.number().int().min(0),
        parentId: uuidSchema.nullable(),
    }))
        .max(200),
});
//# sourceMappingURL=navigation.js.map